// imageShrink.js — makes the report images much smaller before they go into the PDF / Word file.
//
// Why: every layer picture of the report (compass drawings, charts, floor plans...) is a big PNG — about
// 1588x2246 px, 1-3 MB each — and a report easily has 30+ of them, so the PDF and the .docx came out at tens
// of megabytes. As a high-quality JPEG at ~160 dpi the same picture is typically 2-4x smaller (a real floor plan: 591 KB -> 251 KB) and looks the same on paper.
//
// Rules (so nothing visible changes):
//   * only PNG data URLs above `minBytes` are touched; small PNGs and every JPEG stay exactly as they are
//   * the picture is flattened onto WHITE (report pages are white), brought down to at most `maxSide` px on its
//     longest side (1800 px on an A4 page = ~160 dpi, still crisp on paper; the captures were ~212 dpi), then
//     saved as JPEG
//   * if JPEG would not actually be smaller, or anything fails, the ORIGINAL picture is returned unchanged
//   * the same picture is converted only once (cache), however many times a report page uses it

const cache = new Map();

const loadImage = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  im.onload = () => resolve(im);
  im.onerror = () => reject(new Error('image could not be read'));
  im.src = src;
});

export const shrinkImageDataUrl = async (dataUrl, { quality = 0.85, minBytes = 250000, maxSide = 1800 } = {}) => {
  try {
    if (typeof dataUrl !== 'string' || !/^data:image\/png;base64,/i.test(dataUrl)) return dataUrl;
    if (dataUrl.length * 0.75 < minBytes) return dataUrl;
    const key = `${dataUrl.length}:${dataUrl.slice(-96)}:${quality}:${maxSide}`;
    if (cache.has(key)) return cache.get(key);

    const im = await loadImage(dataUrl);
    const scale = Math.min(1, maxSide / Math.max(im.naturalWidth, im.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(im.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(im.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(im, 0, 0, canvas.width, canvas.height);
    const jpeg = canvas.toDataURL('image/jpeg', quality);
    canvas.width = 0; canvas.height = 0;

    const result = jpeg.length < dataUrl.length * 0.9 ? jpeg : dataUrl;
    cache.set(key, result);
    return result;
  } catch (e) {
    return dataUrl;
  }
};
